/* OfflineUpgradeUploadAction.js */

"use strict";

const {unzipSync, strFromU8, gunzipSync} = require("fflate");
const {parseTar} = require("nanotar");
const UploadUpgradeResultsVeilComponent = require("./UploadUpgradeResultsVeilComponent");
const UploadSummaryVeilComponent = require("./UploadSummaryVeilComponent");

const LOADING_MSG = (isSingle) =>
    `Importing your upgrade result ${isSingle ? "file" : "files"}... This could take a moment`;

/**
 * Class representing an action to upload and import offline upgrade result files for IoT devices.
 *
 * OfflineUpgradeUploadAction is responsible for:
 *  - Managing user feedback and UI overlays during the upload/import process.
 *  - Accepting .zip or .tar.gz archives containing IoT upgrade results.
 *  - Extracting and validating the contents of uploaded files.
 *  - Invoking backend logic to process upgrade results.
 *  - Displaying import summaries or errors to the user.
 *
 * Usage:
 *   const action = new OfflineUpgradeUploadAction(logger, view, DeviceEntity, userSession, userFeedback, activityMonitor);
 *   action.run();
 */
class OfflineUpgradeUploadAction {
    /**
     * Create an OfflineUpgradeUploadAction.
     * @param {Object} logger - Logger instance.
     * @param {Object} gameEditor - Reference to the game editor.
     * @param {Object} activeView - Current active view (UI context).
     * @param {Object} userSession - Current user session.
     * @param {Object} userFeedback - Modal user feedback handler.
     * @param {Object} activityMonitor - Activity monitor instance.
     */
    constructor(logger, gameEditor, activeView, userSession, userFeedback, activityMonitor) {
        this._logger = logger;
        this._gameEditor = gameEditor;
        this._activeView = activeView;
        this._modalUserFeedback = userFeedback;
        this._userSession = userSession;
        this._activityMonitor = activityMonitor;
        this._plan = this._gameEditor.getPlan();
        this.run = this.run.bind(this);
    }

    /**
     * Initiates the upload and import workflow for offline upgrade result files.
     * @returns {Promise<void>}
     */
    run() {
        return this._modalUserFeedback
            .displayCustom(new UploadUpgradeResultsVeilComponent(this._logger), {
                dontHide: true,
            })
            .then(async (files) => {
                if (files) {
                    this._activityMonitor.stopPolling();
                    this._showLoadingVeil(LOADING_MSG(files.length > 1));

                    const upgrades = [];
                    for (const file of Array.from(files)) {
                        const upgradeFilename = file.name;
                        if (!upgradeFilename.startsWith("upgrade-")) {
                            continue;
                        }

                        let upgrade = null;
                        if (upgradeFilename.endsWith(".zip")) {
                            upgrade = await this._extractUpgradeFromArchive({
                                file,
                                upgradeFilename,
                                extractEntries: async (file) => {
                                    const arrayBuffer = await file.arrayBuffer();
                                    const uint8 = new Uint8Array(arrayBuffer);
                                    const zipEntries = unzipSync(uint8);
                                    return Object.entries(zipEntries).map(([name, data]) => ({
                                        name,
                                        data,
                                    }));
                                },
                            });
                        } else if (upgradeFilename.endsWith(".tar.gz")) {
                            upgrade = await this._extractUpgradeFromArchive({
                                file,
                                upgradeFilename,
                                extractEntries: async (file) => {
                                    const arrayBuffer = await file.arrayBuffer();
                                    const tarData = gunzipSync(new Uint8Array(arrayBuffer));
                                    const entries = await parseTar(tarData);
                                    return entries.map((entry) => ({
                                        name: entry.name,
                                        data: entry.data,
                                    }));
                                },
                            });
                        } else {
                            return Promise.reject(
                                `The file ${file.name} is not a valid upgrade result file type ` +
                                    `(only .zip and .tar.gz are supported). ` +
                                    `Please check the selected files and try again.`
                            );
                        }

                        if (upgrade) {
                            upgrades.push(upgrade);
                        }
                    }

                    return Promise.resolve(upgrades);
                }

                return Promise.resolve([]);
            })
            .then((upgrades) => {
                if (upgrades.length === 0) {
                    return Promise.reject(
                        "None of the selected upgrade result files is valid. Please check the selected .zip or .tar.gz files."
                    );
                }

                return this._gameEditor.getPlan().runOdmUpgradeWork({upgrades});
            })
            .then((importResults) => {
                this._activityMonitor.startPolling();
                return this._modalUserFeedback.displayCustom(
                    new UploadSummaryVeilComponent(importResults, this._logger)
                );
            })
            .catch((error) => {
                this._activityMonitor.startPolling();
                if (error && error.aborted) {
                    this._abortUpload();
                } else if (error && !error.userRejected) {
                    this._modalUserFeedback.displayError({error: {errorMsg: error, errorCode: ""}});
                }
            });
    }

    /**
     * Extracts upgrade data from an archive file.
     * @param {Object} params
     * @param {File} params.file - The archive file.
     * @param {string} params.upgradeFilename - File name.
     * @param {Function} params.extractEntries - Function to extract entries from archive.
     * @returns {Promise<Object|null>} Upgrade object or null if invalid.
     */
    async _extractUpgradeFromArchive({file, upgradeFilename, extractEntries}) {
        const upgrade = {
            upgradeFilename,
        };

        const upgradeFilenameWithoutExtension = upgradeFilename.replace(/\.zip$|\.tar\.gz$/, "");

        const entries = await extractEntries(file);

        for (const {name, data} of entries) {
            try {
                const fileMap = {
                    "commandLog.json": () => (upgrade.commandLog = JSON.parse(strFromU8(data))),
                    "upgrade.json": () => (upgrade.upgrade = JSON.parse(strFromU8(data))),
                    "discovery.json": () => (upgrade.discovery = JSON.parse(strFromU8(data))),
                    "logs/CBS.log": () => (upgrade.cbsLog = strFromU8(data)),
                    "logs/dpkg.log": () => (upgrade.dpkgLog = strFromU8(data)),
                    "logs/apt/history.log": () => (upgrade.aptHistoryLog = strFromU8(data)),
                    "logs/apt/term.log": () => (upgrade.aptTermLog = strFromU8(data)),
                };

                const baseName = name.replace(`${upgradeFilenameWithoutExtension}/`, "");
                const handler = fileMap[baseName];

                if (handler) {
                    handler();
                }
            } catch (e) {
                continue;
            }
        }

        if (!upgrade.commandLog || !upgrade.upgrade || !upgrade.discovery) {
            return null;
        }

        return upgrade;
    }

    /**
     * Shows a loading veil with a given message.
     * @param {string} message
     * @returns {Promise}
     */
    _showLoadingVeil(message) {
        return this._modalUserFeedback.displayLoading({message, dontHide: true});
    }

    /**
     * Hides the current loading veil.
     * @returns {Promise}
     */
    _hideLoadingVeil() {
        return this._modalUserFeedback.hideDisplayed();
    }

    /**
     * Cleans up any resources and hides UI overlays.
     */
    shutdown() {
        this._modalUserFeedback.hideDisplayed();
        this._gameEditor = undefined;
        this._modalUserFeedback = undefined;
        this._logger = undefined;
        this._activeView = undefined;
        this._userSession = undefined;
        this._activityMonitor = undefined;
        this._plan = undefined;
    }

    /**
     * Aborts the current upload process and hides overlays.
     * @returns {Promise}
     */
    _abortUpload() {
        console.log("OfflineUpgradeUploadAction.run() - Aborting...");
        return this._modalUserFeedback.hideDisplayed();
    }
}

module.exports = OfflineUpgradeUploadAction;

OfflineUpgradeUploadAction.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "gameEditor.activeView",
    "$teve.userSession",
    "$teve.ui.userFeedback",
    "$teve.activityMonitor",
];

OfflineUpgradeUploadAction.$displayDetails = {
    type: "Action",
    name: "OfflineUpgradeUploadAction",
    icon: "devicemanager.offline_discovery_upload",
    shown: false,
    title: "Import upgrade results",
    order: 3,
};
